import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ConversationSummaryStatesEnum} from '../../../../../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {TeammateModel} from '../../../../../../../libs/shared/core/src/models/api/teammateModel';
import {DataEventActionsEnum} from '../../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {useDataEventTracking} from '../../../../../../../libs/shared/core/src/services/tracking/dataEventTrackingContext';
import {IconButton} from '../../../../../../../libs/shared/ui/src/components/buttons/iconButton';
import {InlineLinkButton} from '../../../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {Icon} from '../../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../../../../srcCommon/browser/openInBrowser';
import {useAppDispatch} from '../../../../../../helpers/appReduxHelpers';
import {dismissConversationSummaryErrorAction} from '../../../../../../store/data/conversations/conversationsActions';
import {helpCenterUrl} from '../../../../../../store/stateless/navigationActions';

/*
 * Constants.
 */

const helpCenterRateLimitUrl = `${helpCenterUrl}/en/articles/2003#are_there_rate_limits`;

/*
 * Props.
 */

interface ConversationHeaderAiSummarizeErrorStateProps {
  conversationId: number;
  summaryState: ConversationSummaryStatesEnum;
  teammate: TeammateModel;
}

/*
 * Style.
 */

const StyledContentWrapperDiv = styled.div`
  height: 38px;
  width: 100%;
  display: flex;
  flex-flow: row;
  gap: 8px;
  align-items: center;
  padding: 0px 12px;
  font-weight: ${fontWeights.medium};
  background-color: ${(p) => p.theme.palette.orange.shade20};
  border-radius: 8px;
`;

const StyledSummarizeErrorContent = styled.span`
  margin-right: auto;
`;

/*
 * Component.
 */

export const ConversationHeaderAiSummarizeErrorState: FC<ConversationHeaderAiSummarizeErrorStateProps> = ({
  conversationId,
  summaryState,
  teammate,
}) => {
  const theme = useUserTheme();
  const {trackDataEvent} = useDataEventTracking();
  const dispatch = useAppDispatch();

  const onRateLimitLearnMoreClick = () => {
    openInBrowser(helpCenterRateLimitUrl);
  };

  const onClick = () => {
    dispatch(dismissConversationSummaryErrorAction(conversationId));
    trackDataEvent(DataEventActionsEnum.COLLAPSE, ViewerDataEventObject.CONVERSATION_SUMMARY_ERROR, {
      teammate_id: teammate.id,
      conversation_id: conversationId,
      source: 'manual',
    });
  };

  const label =
    summaryState === ConversationSummaryStatesEnum.FAILED_RATE_LIMITED ? (
      <Trans id="ai-summarize-error-rate-limit">
        Cannot generate summary due to daily rate limit. Try again later.{' '}
        <InlineLinkButton onClick={onRateLimitLearnMoreClick}>Learn more</InlineLinkButton>
      </Trans>
    ) : (
      <Trans id="ai-summarize-error-generic">
        An error occurred generating the summary. Please try again later or contact Front Support.
      </Trans>
    );

  return (
    <StyledContentWrapperDiv>
      <Icon name="warningFilledSmall" color={theme.palette.orange.shade50} />
      <StyledSummarizeErrorContent>{label}</StyledSummarizeErrorContent>
      <IconButton
        iconName="closeSmall"
        color={theme.greys.shade80}
        hoverColor={theme.greys.shade80}
        label={<Trans id="ai-summarize-error-dismiss">Dismiss error</Trans>}
        onClick={onClick}
      />
    </StyledContentWrapperDiv>
  );
};
